import { CheckCircle } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import PendingIcon from "@mui/icons-material/Pending";
import {
	AppBar,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import { fetchGroups } from "actions/groupActions";
import { fetchLicenseGroups } from "actions/licenseActions";
import { createOrder } from "actions/orderActions";
import { createRequester } from "actions/requesterActions";
import {
	createGuestInvitation,
	createUser,
	sendMail,
	sendSms,
	setManager,
} from "actions/userActions";
import { ReactComponent as IronstoneLogo } from "assets/logo/ironstone_logo_title.svg";
import clsx from "clsx";
import {
	PrimaryButton,
	PrimaryFormButton,
	SecondaryButton,
} from "components/Common/Buttons/Button";
import { PrimaryDialog } from "components/Common/Dialogs/Dialog";
import { InternalLink } from "components/Common/Links";
import { getCode } from "country-list";
import { selectCustomerDetails } from "features/customer";
import { groupSelectors } from "features/groups";
import { selectHardwarePurchaseType } from "features/hardware/hardwarePage";
import {
	clearCartItems,
	productsSelectors,
	selectCartCountBySku,
} from "features/hardware/products";
import { selectEnrichedLicenseGroups } from "features/licenses/licenses";
import { selectDefaultMailDomain } from "features/mailDomains";
import { useApiNoCache, useAppSelector, useAuth } from "hooks/hooks";
import _ from "lodash";
import { useEffect, useState } from "react";
import {
	Employee,
	FormEmployee,
	License,
	Loaders,
	NewEmployeeFormFields,
	OnboardingEntity,
	User,
} from "types";
import { DeliveryOption, OnboardingStatus } from "utilities/constants/enums";
import { NAVIGATION_NAMES } from "utilities/constants/pages";
import { filterDefaultAccessGroups } from "utilities/groupUtils/accessGroupUtils";
import {
	mapObjectStringValues,
	trimAndRemoveDuplicateSpacesBetweenWords,
} from "utilities/stringFormattingUtility";
import { SelectHardware } from "../../Hardware/SelectHardware";
import { Groups } from "../EmployeeDialogSteps/Groups";
import { PersonalInfo } from "../EmployeeDialogSteps/PersonalInfo";
import { Receipt } from "../EmployeeDialogSteps/Receipt/Receipt";
import { Summary } from "../EmployeeDialogSteps/Summary";
import { adjustLicenseQuantity } from "../helpers/licenseHelper";
import styles from "./AddEmployeeDialog.module.scss";

interface Props {
	onClose: () => void;
}

// TODO: Move the onboarding state into redux, both steps and data
// Maybe also move isOnboarding into redux, so that we can check on this prop from every prop that checks for isOnboarding
const AddEmployeeDialog = ({ onClose }: Props) => {
	const customer = useAppSelector(selectCustomerDetails);
	const cartItems = useAppSelector(selectCartCountBySku);
	const productsById = useAppSelector(productsSelectors.selectEntities);
	const hardwarePurchaseType = useAppSelector(selectHardwarePurchaseType);

	const {
		dispatch,
		auth,
		auth: {
			account: { localAccountId },
		},
	} = useAuth();

	useApiNoCache(fetchLicenseGroups);

	useEffect(() => {
		dispatch(fetchGroups({ auth, groupType: "ALL", groupName: "" }));
	}, [dispatch, auth]);

	const groups = useAppSelector(groupSelectors.selectAll);

	const { hasKomplettRelationship } = useAppSelector(selectCustomerDetails) ?? {};

	const shouldShowProducts = hasKomplettRelationship;

	const [newEmployeeData, setNewEmployeeData] = useState<NewEmployeeFormFields>({
		givenName: "",
		surname: "",
		mailPrefix: "",
		mailSuffix: "",
		privateEmail: "",
		password: "",
		officeLocation: "",
		streetAddress: "",
		department: "",
		mobilePhone: "+47",
		manager: localAccountId,
		country: "",
		employeeCountry: "",
		shippingCountry: "",
		city: "",
		postalCode: "",
		licenseGroups: [],
		addUserToGroups: [],
		addUserToAccessGroups: groups.filter(filterDefaultAccessGroups),
		chosenLicenses: [],
		agreedToExtendMissingLicenses: false,
		hardwareNote: "",
		companyName: customer?.departmentName ?? "",
		deliveryOption: DeliveryOption.B2B,
	});

	useEffect(() => {
		setNewEmployeeData((state) => ({
			...state,
			addUserToAccessGroups: groups.filter(filterDefaultAccessGroups),
		}));
	}, [groups]);

	const [activeStep, setActiveStep] = useState(0);

	const availableLicenseGroups = useAppSelector(selectEnrichedLicenseGroups);

	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [isFormDataDirty, setIsFormDataDirty] = useState<boolean>(false);

	const [loaders, setLoaders] = useState<Loaders>({} as Loaders);
	const [showLoadingDialog, setShowLoadingDialog] = useState(false);

	const [pendingNavigation, setPendingNavigation] = useState<null | (() => void)>(null);

	const handleOnboardingMyBundlesClick = (navigate: () => void) => {
		if (activeStep === 2) {
			setPendingNavigation(() => navigate);
			setShowConfirmDialog(true);
		} else {
			navigate();
		}
	};

	const handleSubmit = (name: OnboardingEntity) => (payload?: any) => {
		let status = OnboardingStatus.Failed;
		if (payload?.success || !payload?.error) {
			status = OnboardingStatus.Complete;
		}
		setTimeout(() => {
			setLoaders((loaders) => ({
				...loaders,
				[name]: {
					...loaders[name],
					status,
				},
			}));
		}, 2000);
		return payload;
	};

	const startSubmitting = (name: OnboardingEntity) =>
		setTimeout(
			() =>
				setLoaders((loaders) => ({
					...loaders,
					[name]: { ...loaders[name], status: OnboardingStatus.InProgress },
				})),
			500,
		);

	const isSubmitting = Object.values(loaders).some(
		({ status }) => status === OnboardingStatus.InProgress,
	);

	useEffect(() => {
		// Show the loading dialog once isSubmitting is true
		// User will need to close the loading dialog manually
		if (isSubmitting) {
			setShowLoadingDialog(true);
		}
	}, [isSubmitting]);

	const closeBothDialogs = () => {
		setShowConfirmDialog(false);
		onClose();
		dispatch(clearCartItems());
	};

	const onCloseClicked = () => {
		if (activeStep === 4) {
			onClose();
			return;
		}
		if (!isFormDataDirty) {
			onClose();
			return;
		}
		setShowConfirmDialog(true);
	};

	const updateEmployeeData = ({ manager, ...newData }: FormEmployee) => {
		const data = {
			...newEmployeeData,
			...newData,
			manager: manager?.id ?? newEmployeeData.manager,
			country: newData.country ?? newEmployeeData.country,
		};
		setNewEmployeeData(data);
		setActiveStep(1);
	};

	const { name: defaultMailDomain = "" } = useAppSelector(selectDefaultMailDomain) ?? {};

	useEffect(() => {
		const { mailSuffix } = newEmployeeData;

		if (mailSuffix?.length > 0) {
			return;
		}

		setNewEmployeeData((newEmployeeData) => ({
			...newEmployeeData,
			mailSuffix: defaultMailDomain,
		}));
	}, [defaultMailDomain, newEmployeeData]);

	const steps = [
		{ label: "Details", title: "User details" },
		{
			label: "Add to groups",
			title: "Add to groups",
			optional: true,
		},
		{
			label: "Pick hardware",
			title: "Pick hardware",
			optional: true,
		},
		{ label: "Summary", title: "Summary" },
		{ label: "Receipt", title: "Receipt" },
	];

	const getStepsContent = (currentStep: number) => {
		switch (currentStep) {
			case 0:
				return (
					<DialogContent>
						<PersonalInfo
							isOnboarding={true}
							employeeData={newEmployeeData}
							saveForm={updateEmployeeData}
							setIsFormDataDirty={setIsFormDataDirty}
							showShippingDetails={false}
						/>
					</DialogContent>
				);
			case 1:
				return (
					<DialogContent>
						<Groups
							employeeData={newEmployeeData}
							updateEmployeeData={updateEmployeeData}
						/>
					</DialogContent>
				);
			case 2:
				// Dialog content nests sticky searchbar in container in such a way that it does not stick on scroll
				return (
					<Grid container mt={8} className={styles.hardwarePageContainer}>
						<SelectHardware
							isOnboarding={true}
							onboardingCheckoutMethod={() => setActiveStep(activeStep + 1)}
							onboardingMyBundlesClick={handleOnboardingMyBundlesClick}
						/>
					</Grid>
				);
			case 3:
				return (
					<DialogContent>
						<Summary newEmployeeData={newEmployeeData} saveForm={onFinish} />{" "}
					</DialogContent>
				);
			case 4:
				return (
					<DialogContent>
						<Receipt newEmployeeData={newEmployeeData} loaders={loaders} />
					</DialogContent>
				);
			default:
				break;
		}
	};

	const onFinish = async ({ manager: { id }, ...formData }: FormEmployee) => {
		const finalEmployeeData = { ...newEmployeeData, ...formData } as
			| FormEmployee
			| NewEmployeeFormFields;
		finalEmployeeData.manager = id;
		mapObjectStringValues(finalEmployeeData, trimAndRemoveDuplicateSpacesBetweenWords);
		setNewEmployeeData(finalEmployeeData as NewEmployeeFormFields);
		await onPostNewEmployee(finalEmployeeData);
		setActiveStep(activeStep + 1);
	};

	const dispatchSendSms = ({ password, mobilePhone }: Employee) =>
		dispatch(
			sendSms({
				auth,
				body: {
					message: password,
					recipientMobilePhone: mobilePhone.includes("+")
						? mobilePhone
						: `+47${mobilePhone}`,
				},
			}),
		);

	const handleRetrySendSms = () => {
		setLoaders((loaders) => ({
			...loaders,
			sms: { ...loaders.sms, status: OnboardingStatus.InProgress },
		}));
		dispatchSendSms(newEmployeeData).then(handleSubmit("sms"));
	};

	const dispatchSendMail = ({
		givenName,
		surname,
		mailPrefix,
		mailSuffix,
		privateEmail,
		streetAddress,
		mobilePhone,
		city,
		postalCode,
		deliveryOption,
	}: Employee) =>
		dispatch(
			sendMail({
				auth,
				body: {
					mailType: "ONBOARDING",
					recipientMail: privateEmail,
					recipientName: `${givenName} ${surname}`,
					messageBody: {
						onboardedUserMail: `${mailPrefix.toLowerCase()}@${mailSuffix}`,
						onboardedUserMobilePhone: mobilePhone.includes("+")
							? mobilePhone
							: `+47${mobilePhone}`,
						firmName: customer?.departmentName ?? "",
						hardwareBought: Object.entries(cartItems).map(
							([sku, quantity]) =>
								`https://www.komplett.no/product/${sku}` +
								((quantity as number) > 1 ? ` (${quantity})` : ""),
						),
						deliveryOption,
						shippingAddress: {
							street: streetAddress,
							postalCode,
							city,
						},
					},
				},
			}),
		);

	const postUserToCustomerTenant = ({
		mailPrefix,
		mailSuffix,
		addUserToGroups,
		addUserToAccessGroups,
		employeeCountry,
		shippingCountry,
		...employeeData
	}: FormEmployee | NewEmployeeFormFields) => {
		const accessAndLicenseGroups = [
			...addUserToGroups,
			...addUserToAccessGroups.map((group) => group.id),
		];

		const body = {
			...employeeData,
			country: employeeCountry, // Use employeeCountry for user creation
			mail: `${mailPrefix.toLowerCase()}@${mailSuffix}`,
			addUserToGroups: accessAndLicenseGroups,
		};

		return dispatch(createUser({ auth, body }));
	};

	const initializeLoaders = (
		shouldSubmitLicenses: boolean,
		shouldSubmitOrder: boolean,
		shouldSendMail: boolean,
	) => {
		const includedLoaders = {
			user: {
				label: "Creating user...",
				response: "User created!",
				pending: "Waiting to create user...",
				status: OnboardingStatus.Initial,
			},
		} as Loaders;

		if (shouldSubmitLicenses) {
			includedLoaders.licenses = {
				label: "Adding licenses...",
				response: "Licenses added!",
				pending: "Waiting to add licenses...",
				error: "License assignment failed!",
				status: OnboardingStatus.Initial,
				suggestion: (
					<Typography variant="body1">
						You can retry adding license groups later by editing the user within{" "}
						<InternalLink onClick={onClose}>Manage Employees</InternalLink>
					</Typography>
				),
			};
		}

		if (shouldSubmitOrder) {
			includedLoaders.order = {
				label: "Creating hardware order...",
				response: "Hardware order created!",
				pending: "Waiting to create order...",
				error: "Onboarding mail failed!",
				status: OnboardingStatus.Initial,
				suggestion: (
					<Typography variant="body1">
						You can retry ordering through{" "}
						<InternalLink href={NAVIGATION_NAMES.HARDWARE.path}>
							Buy Hardware
						</InternalLink>
					</Typography>
				),
			};
		}

		if (shouldSendMail) {
			includedLoaders.mail = {
				label: "Sending onboarding mail...",
				response: "Onboarding mail sent!",
				pending: "Waiting to send onboarding mail...",
				error: "We were not able to send onboarding mail.",
				status: OnboardingStatus.Initial,
			};
		}

		includedLoaders.sms = {
			label: "Sending SMS with password...",
			response: "SMS with password sent!",
			pending: "Waiting to send onboarding SMS...",
			error: "Onboarding sms failed!",
			status: OnboardingStatus.Initial,
			suggestion: (
				<Typography variant="body1">
					<Typography variant="body1" mb={2}>
						You can retry sending an SMS with the password{" "}
						<Typography variant="description" color="text.secondary">
							({newEmployeeData.password})
						</Typography>{" "}
						<InternalLink
							variant="body1"
							marginBottom={0.35}
							onClick={handleRetrySendSms}
						>
							here
						</InternalLink>{" "}
						or you can send it manually to the recipients phone number.
						<Typography variant="description" color="text.secondary">
							({newEmployeeData.mobilePhone})
						</Typography>{" "}
					</Typography>
				</Typography>
			),
		};

		setLoaders(includedLoaders);
	};

	const onPostNewEmployee = async (employeeData: FormEmployee | NewEmployeeFormFields) => {
		const {
			givenName,
			surname,
			privateEmail,
			officeLocation,
			streetAddress,
			mobilePhone,
			city,
			postalCode,
			country,
			shippingCountry,
			addUserToGroups,
			manager,
			hardwareNote,
			companyName,
			deliveryOption,
		} = employeeData;

		const shouldSubmitLicenses = !_.isEmpty(addUserToGroups);
		const shouldSubmitOrder = !_.isEmpty(cartItems);
		const shouldSendMail = !!privateEmail;

		initializeLoaders(shouldSubmitLicenses, shouldSubmitOrder, shouldSendMail);

		startSubmitting("user");
		shouldSubmitLicenses && startSubmitting("licenses");

		const userResponse = await postUserToCustomerTenant(employeeData);

		const payload = userResponse.payload as User & { addedToGroups: string[] };

		const { id, displayName, mail, userPrincipalName } = payload;

		const user = {
			id,
			displayName,
			givenName,
			mail,
			officeLocation,
			surname,
			userPrincipalName,
			groups: payload.addedToGroups.map((id) => availableLicenseGroups[id]),
			chosenLicenses: newEmployeeData.chosenLicenses ?? [],
		};

		const needsProvisioning = shouldSubmitLicenses
			? user.chosenLicenses
					.filter((license: License) => license.needsProvisioning)
					.map((license) => license.chosenVariant)
			: [];

		shouldSubmitOrder && startSubmitting("order");

		const handleManagerAndUserGuestInvitationAndFreshServiceRequester = () =>
			Promise.all([
				dispatch(setManager({ auth, id, body: { id: manager } })),
				dispatch(
					createGuestInvitation({
						auth,
						body: {
							invitedUserEmailAddress: mail,
							invitedUserDisplayName: displayName,
							companyName: customer.departmentName,
						},
					}),
				),
				dispatch(
					createRequester({
						auth,
						body: {
							primaryEmail: mail,
							firstName: givenName,
							lastName: surname,
							isAdmin: false,
						},
					}),
				),
			]).finally(handleSubmit("user")); // Display that user creation succeeded even if these steps failed

		const handleLicenseAdjustment = async () => {
			let task = Promise.resolve({ success: true });

			if (!shouldSubmitLicenses) {
				return task;
			}

			if (needsProvisioning.length !== 0) {
				task = adjustLicenseQuantity({
					auth,
					dispatch,
					user,
					subscriptionVariants: needsProvisioning,
				});
			}

			return task.then(handleSubmit("licenses"));
		};

		const handleOrder = async () => {
			if (!shouldSubmitOrder) {
				return Promise.resolve();
			}

			return dispatch(
				createOrder({
					auth,
					id,
					body: {
						messageToSalesRep: "",
						purchaserFirmName:
							deliveryOption === DeliveryOption.B2B ? companyName : displayName,
						deliveryOption,
						purchaserFirmAddress: streetAddress,
						purchaserFirmPostalCode: postalCode,
						purchaserFirmCity: city,
						purchaserFirmCountryCode:
							getCode(shippingCountry ?? country ?? "N/A") ?? "NO",
						contactPersonName: displayName,
						contactPersonEmail: privateEmail,
						contactPersonPhone: mobilePhone,
						orderLines: Object.entries(cartItems)
							.map(([sku, quantity]) => ({ ...productsById[sku], quantity }))
							.map(
								({ sku, quantity, displayName, priceInfo: { priceNet } = {} }) => ({
									sku,
									quantity,
									displayName,
									price: priceNet,
								}),
							),
						hardwareNote,
						isLeasingOrder: hardwarePurchaseType === "Lease",
					},
				}),
			).then(handleSubmit("order"));
		};

		await Promise.all([
			handleManagerAndUserGuestInvitationAndFreshServiceRequester(),
			handleLicenseAdjustment(),
			handleOrder(),
		]);

		shouldSendMail && startSubmitting("mail");
		startSubmitting("sms");

		if (shouldSendMail) {
			await dispatchSendMail(employeeData).then(handleSubmit("mail"));
		}

		await dispatchSendSms(employeeData).then(handleSubmit("sms"));
	};

	useEffect(() => {
		// Always scroll to top when current step changes
		const dialogContainer = document.querySelector(`.${styles.dialogContainer}`);

		if (dialogContainer) {
			dialogContainer.scrollTo(0, 0);
		}
	}, [activeStep]);

	return (
		<Dialog
			classes={{ paper: styles.dialogContainer }}
			onClose={onCloseClicked}
			aria-labelledby="dialog-add-employee"
			open
			fullScreen
			disableEnforceFocus
		>
			{showConfirmDialog && (
				<PrimaryDialog
					title="Leave this page?"
					size="small"
					primaryButtonText="Leave"
					secondaryButtonText="Stay"
					onLeaveAction={() => setShowConfirmDialog(false)}
					primaryAction={() => {
						setShowConfirmDialog(false);
						if (pendingNavigation) {
							pendingNavigation();
							setPendingNavigation(null);
						} else {
							closeBothDialogs();
						}
					}}
				>
					There are unsaved changes. Do you want to leave without saving?
				</PrimaryDialog>
			)}
			<Grid container classes={{ root: styles.dialogGridContainer }}>
				<AppBar color="inherit" position="fixed" classes={{ root: styles.appBarTop }}>
					<Grid
						container
						alignItems="center"
						classes={{ root: styles.appBarTopContainer }}
					>
						<Grid item xs={4} className={styles.dialogTitle}>
							<DialogTitle
								variant="h2"
								className={clsx(styles.stepCounter, styles.showForSmallScreens)}
							>
								{`Step ${activeStep + 1}/${steps.length}: `}
							</DialogTitle>
							<DialogTitle variant="h2">{steps[activeStep].title}</DialogTitle>
						</Grid>
						<Grid item xs={4} className={styles.hideForSmallerScreens}>
							{activeStep < 4 ? (
								<Stepper activeStep={activeStep}>
									{steps.map((step) => {
										const disabled =
											!shouldShowProducts && step.label === "Pick hardware";
										const label = disabled
											? "Hardware solution required"
											: "Optional";
										return (
											<Step key={step.label}>
												<StepLabel
													classes={{
														label: disabled
															? styles.disabledStepLabel
															: step.optional
																? styles.stepLabel
																: "",
													}}
												>
													<Typography
														classes={{
															root: disabled ? styles.disabled : "",
														}}
													>
														{step.label}
													</Typography>
													{step.optional && (
														<Typography variant="caption">
															{label}
														</Typography>
													)}
												</StepLabel>
											</Step>
										);
									})}
								</Stepper>
							) : (
								<Grid container justifyContent="center">
									<IronstoneLogo className={styles.logo} />
								</Grid>
							)}
						</Grid>
						<Grid item xs={4} container justifyContent="flex-end" alignItems="center">
							<Typography variant="h2" mr={4} className={styles.hideForMediumScreens}>
								Close view
							</Typography>
							<IconButton
								edge="start"
								color="inherit"
								onClick={onCloseClicked}
								aria-label="close"
							>
								<CloseIcon />
							</IconButton>
						</Grid>
					</Grid>
				</AppBar>
				<Grid className={styles.innerContainer}>
					{showLoadingDialog ? (
						<Dialog open>
							<Grid
								container
								direction="column"
								justifyContent="center"
								alignItems="center"
								rowSpacing={4}
								p={3}
							>
								<>
									<Grid container justifyContent="center" pt={4}>
										<Grid item>
											<Typography variant="h3">
												This might take a minute...
											</Typography>
										</Grid>
										<Grid item pl={1}>
											<FreeBreakfastOutlinedIcon />
										</Grid>
									</Grid>
									{Object.values(loaders).map(
										({ label, response, error, status, pending }) => (
											<Grid
												key={label}
												item
												container
												justifyContent="flex-start"
												alignItems="center"
												classes={{ root: styles.loader }}
											>
												{status === OnboardingStatus.InProgress ? (
													<CircularProgress size={30} color="primary" />
												) : status === OnboardingStatus.Complete ? (
													<CheckCircle
														color="success"
														classes={{ root: styles.smallSuccess }}
													/>
												) : status === OnboardingStatus.Failed ? (
													<ErrorIcon
														color="error"
														classes={{ root: styles.smallSuccess }}
													/>
												) : (
													<PendingIcon
														classes={{ root: styles.pending }}
													/>
												)}
												<Typography variant="h5" ml={2}>
													{status === OnboardingStatus.InProgress
														? label
														: status === OnboardingStatus.Complete
															? response
															: status === OnboardingStatus.Failed
																? error
																: pending}
												</Typography>
											</Grid>
										),
									)}
								</>
								<PrimaryButton
									size="medium"
									text="OK"
									action={() => setShowLoadingDialog(false)}
									variantStyle="contained"
									marginTop={3}
									isDisabled={Object.values(loaders).some(
										({ status }) => status === OnboardingStatus.InProgress,
									)}
								/>
							</Grid>
						</Dialog>
					) : (
						<>{getStepsContent(activeStep)}</>
					)}
				</Grid>
				<AppBar color="inherit" position="fixed" classes={{ root: styles.appBarBottom }}>
					<Grid container className={styles.buttonContainer}>
						{activeStep === 0 ? (
							<SecondaryButton
								text="Close"
								size="large"
								variantStyle="outlined"
								action={onClose}
								marginLeft={5}
								marginTop={3}
								marginBottom={3}
							/>
						) : activeStep < 4 ? (
							<SecondaryButton
								text="Go back"
								size="large"
								variantStyle="outlined"
								action={() =>
									setActiveStep(
										activeStep - (activeStep < 3 || shouldShowProducts ? 1 : 2),
									)
								}
								marginLeft={5}
								marginTop={3}
								marginBottom={3}
							/>
						) : null}
						{activeStep === 0 && (
							<PrimaryFormButton
								text="Next: Add to group"
								size="large"
								variantStyle="contained"
								form="personal-data-form"
								onSubmit={() => setActiveStep(activeStep + 1)}
								marginRight={5}
								marginTop={3}
								marginBottom={3}
							/>
						)}
						{activeStep === 1 && (
							<PrimaryButton
								text={shouldShowProducts ? "Next: Pick hardware" : "Next: Summary"}
								size="large"
								variantStyle="contained"
								action={() =>
									setActiveStep(activeStep + (shouldShowProducts ? 1 : 2))
								}
								marginRight={5}
								marginTop={3}
								marginBottom={3}
							/>
						)}
						{activeStep === 2 && (
							<PrimaryButton
								text="Next: Summary"
								size="large"
								variantStyle="contained"
								action={() => setActiveStep(activeStep + 1)}
								marginRight={5}
								marginTop={3}
								marginBottom={3}
							/>
						)}
						{activeStep === 3 && (
							<PrimaryFormButton
								text="Finish"
								size="large"
								variantStyle="contained"
								form="personal-data-form"
								onSubmit={(e: unknown) => onFinish(e as FormEmployee)}
								marginRight={5}
								marginTop={3}
								marginBottom={3}
							/>
						)}
						{activeStep === 4 && (
							<Grid container justifyContent="center">
								<PrimaryButton
									text="Finish"
									size="large"
									variantStyle="contained"
									action={onClose}
									marginRight={5}
									marginTop={3}
									marginBottom={3}
								/>
							</Grid>
						)}
					</Grid>
				</AppBar>
			</Grid>
		</Dialog>
	);
};

export { AddEmployeeDialog };
